import { CardActions, CardContent, CardHeader, Card as MuiCard, styled } from '@mui/material';
import type { CardProps as MuiCardProps, SxProps } from '@mui/material';

import { ImageProps } from 'components/image/types';
import { IconProvider } from 'providers/icon';
import { colors } from 'styles/theme/colors';
import { Image } from 'components/image/Image';

export type CardProps = MuiCardProps & {
  actions?: {
    slot: React.ReactNode;
    sx?: SxProps;
  };
  src?: string;
  header: {
    title: string;
    titleSx?: SxProps;
    action?: React.ReactNode;
    sx?: SxProps;
  };
  icon?: React.ReactNode;
  image?: ImageProps;
  order?: {
    header: number;
    content: number;
    actions: number;
    image: number;
  };
};

const defaultOrder = {
  image: 1,
  header: 2,
  content: 3,
  actions: 4,
};

export const Card = ({
  sx,
  children,
  actions,
  image,
  icon,
  header,
  order = defaultOrder,
  src,
}: CardProps) => {
  return (
    <StyledCard sx={sx} variant="outlined">
      {image?.src ? (
        <MediaContainer>
          <Image sx={{ order: order.image }} {...image} />
        </MediaContainer>
      ) : (
        icon && (
          <MediaContainer>
            <IconProvider value={{ size: 100, weight: 'light', color: colors.grey[600] }}>
              <div data-testId="card-icon">{icon}</div>
            </IconProvider>
          </MediaContainer>
        )
      )}
      {header && (
        <Header
          sx={{ ...header.sx, order: order.header }}
          action={header.action}
          title={header.title}
          slotProps={{ title: { variant: 'bodyMedium', sx: { fontWeight: 700 } } }}
        />
      )}
      <Content sx={{ order: order.content }}>{children}</Content>
      {actions?.slot && (
        <Actions sx={{ ...actions.sx, order: order.actions }}>{actions.slot}</Actions>
      )}
      {src && <Link data-testId="card-link" href={src} />}
    </StyledCard>
  );
};

const Link = styled('a')({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  zIndex: 99,
  borderRadius: '0.5rem',
  '&:focus': {
    outlineColor: colors.brand[500],
    outlineWidth: '2px',
    outlineOffset: '-1px',
  },
});

const StyledCard = styled(MuiCard)({
  position: 'relative',
  borderRadius: '0.5rem',
  padding: '1rem',
  borderColor: colors.brand[200],
  background: colors.beige[200],
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    backgroundColor: colors.mintGreen[200],
  },
});

const Header = styled(CardHeader)(({ theme }) => ({
  padding: '0 0 0 0',
  paddingBottom: theme.spacing(2),
}));

const Content = styled(CardContent)({
  padding: 0,
  fontSize: '0.875rem',
  '&:last-child': {
    paddingBottom: 0,
  },
});

const Actions = styled(CardActions)(({ theme }) => ({
  padding: '0',
  paddingTop: theme.spacing(5),
  fontSize: '0.875rem',
}));

const MediaContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(5),
}));
